import styled, { css } from 'styled-components';
import { TokenName } from '@mewssystems/design-tokens';
import { getTextTokenPreset, getToken, VISUALLY_HIDDEN } from '@optimus-web/common';
import { ANIMATION_CURVE } from '@optimus-web/local-tokens';
import {
    getActiveBackgroundColor,
    getActiveBorderColor,
    getBackgroundColor,
    getBorderColor,
    getHoverBackgroundColor,
    getHoverBorderColor,
} from './getCheckboxStyles';

const transitionDuration = '90ms';
export const CheckboxContainer = styled.div``;

interface CheckboxElementProps {
    disabled?: boolean;
    withoutLabel?: boolean;
}

export const InputWrapper = styled.label<CheckboxElementProps>`
    display: inline-flex;
    position: relative;
    color: ${getToken(TokenName.TEXT_STATIC_PRIMARY)};

    ${(props) =>
        !props.withoutLabel &&
        css`
            padding: ${getToken(TokenName.SPACING_50)}px 0;
        `}

    ${(props) =>
        props.disabled &&
        css`
            color: ${getToken(TokenName.TEXT_DISABLED)};
        `}
`;

export const CheckboxElement = styled.input`
    ${VISUALLY_HIDDEN}
`;

export interface CheckMarkBackgroundElementProps {
    checked?: boolean;
    disabled?: boolean;
    error?: boolean;
    small?: boolean;
    indeterminate?: boolean;
    withoutLabel?: boolean;
}

export const CheckMarkBackgroundElement = styled.button<CheckMarkBackgroundElementProps>`
    display: inline-flex;
    position: relative;
    align-items: center;
    justify-content: center;
    height: ${getToken(TokenName.SIZING_200)}px;
    width: ${getToken(TokenName.SIZING_200)}px;
    min-width: ${getToken(TokenName.SIZING_200)}px;
    padding: 0;
    border: none;
    background-color: transparent;
    cursor: ${(props) => (props.disabled ? 'not-allowed' : 'pointer')};
    transition: background-color ${transitionDuration} ${ANIMATION_CURVE.fastOutLinearIn};

    &::before {
        content: '';
        background-color: ${(props) => getBackgroundColor(props)};
        position: absolute;
        top: 0;
        left: 0;
        bottom: 0;
        right: 0;
        border: ${getToken(TokenName.BORDER_WIDTH_150)}px solid ${(props) => getBorderColor(props)};
        border-radius: ${getToken(TokenName.BORDER_RADIUS_25)}px;
        box-sizing: border-box;
        transition: border-color ${transitionDuration} ${ANIMATION_CURVE.fastOutLinearIn},
            background-color ${transitionDuration} ${ANIMATION_CURVE.fastOutLinearIn};
    }

    &:focus-visible {
        box-shadow: ${getToken(TokenName.FOCUS_OFFSET)};
        border-radius: ${getToken(TokenName.BORDER_RADIUS_25)}px;
        outline: none;
    }

    &:hover {
        &::before {
            border-color: ${(props) => getHoverBorderColor(props)};
            background-color: ${(props) => getHoverBackgroundColor(props)};
        }
    }

    &:active {
        &::before {
            border-color: ${(props) => getActiveBorderColor(props)};
            background-color: ${(props) => getActiveBackgroundColor(props)};
        }
    }

    ${({ small, theme, withoutLabel }) =>
        !withoutLabel &&
        css`
            margin-top: ${small ? `${theme.designTokens.SPACING_25}px` : `${theme.designTokens.SPACING_50}px`};
        `}

    ${({ disabled, indeterminate, theme }) =>
        indeterminate &&
        css`
            &::after {
                content: '';
                position: absolute;
                width: 10px;
                height: 2px;
                left: calc(50% - 10px / 2);
                top: calc(50% - 2px / 2);
                background-color: ${disabled
                    ? theme.designTokens.TEXT_DISABLED
                    : theme.designTokens.TEXT_STATIC_INVERSE};
                border-radius: 1px;
                z-index: 2;
            }
        `}
`;

interface CheckMarkSvgElementProps {
    checked?: boolean;
    indeterminate?: boolean;
}

export const CheckMarkSvgElement = styled.svg<CheckMarkSvgElementProps>`
    opacity: 0;
    transition: opacity ${transitionDuration} ${ANIMATION_CURVE.linearOutSlowIn};
    position: relative;
    z-index: 1;

    ${({ checked, indeterminate }) =>
        checked &&
        !indeterminate &&
        css`
            opacity: 1;
        `}
`;

interface CheckMarkPathElementProps {
    checked?: boolean;
    disabled?: boolean;
}

export const CheckMarkPathElement = styled.path<CheckMarkPathElementProps>`
    fill: ${({ disabled, theme }) =>
        disabled ? theme.designTokens.TEXT_DISABLED : theme.designTokens.TEXT_STATIC_INVERSE};
`;

interface LabelElementProps {
    small?: boolean;
    required?: boolean;
}

export const LabelElement = styled.div<LabelElementProps>`
    ${({ small }) => (small ? getTextTokenPreset('BODY_M') : getTextTokenPreset('BODY_L'))}
    align-self: flex-end;
    padding-left: ${({ theme }) => theme.designTokens.SPACING_150}px;
`;
